import { useEffect, useRef, useState } from 'react';
import type { CompanyProfile, MessageCitation, ReadMoreLink, RichBlock } from '@prakash-software/shared';
import { RichText } from '../../components/RichText';
import {
  assistantErrorMessage,
  playgroundReply,
  type PlaygroundAnswer,
  type PlaygroundTurn,
} from '../../lib/assistant-api';
import styles from './admin.module.css';
import pg from './Playground.module.css';

interface Bubble {
  role: 'user' | 'assistant';
  text: string;
  richText?: RichBlock[];
  citations?: MessageCitation[];
  readMore?: ReadMoreLink[];
  matched?: number | null;
  gap?: PlaygroundAnswer['gap'];
}

/**
 * Playground: chat with the site's assistant exactly as a visitor would meet
 * it (instructions, company profile, knowledge base, safety rules), with the
 * sources behind each answer shown. Nothing is stored. With unsaved edits on
 * Company & instructions, the admin can test them before saving.
 */
export function PlaygroundSection({
  siteId,
  starters,
  unsaved,
}: {
  siteId: string;
  starters: string[];
  /** Unsaved instructions / profile edits, or null when there are none. */
  unsaved: { systemPrompt: string | null; companyProfile: CompanyProfile | null } | null;
}) {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useDraft, setUseDraft] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [bubbles, busy]);

  async function send(text: string) {
    const question = text.trim();
    if (!question || busy) return;
    const next: Bubble[] = [...bubbles, { role: 'user', text: question }];
    setBubbles(next);
    setInput('');
    setError(null);
    setBusy(true);
    try {
      const turns: PlaygroundTurn[] = next.map((b) => ({ role: b.role, content: b.text }));
      const answer = await playgroundReply(siteId, turns, unsaved && useDraft ? unsaved : undefined);
      setBubbles([
        ...next,
        {
          role: 'assistant',
          text: answer.text,
          richText: answer.richText,
          citations: answer.citations,
          readMore: answer.readMore,
          matched: answer.matched,
          gap: answer.gap,
        },
      ]);
    } catch (err) {
      setError(assistantErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Playground</p>
      <p className={styles.hint} style={{ marginTop: 0 }}>
        Ask the assistant what your visitors would ask. It answers exactly as on your website — same
        instructions, company profile and data sources — and shows what each answer was based on.
        Nothing here is saved or shown to anyone, and it doesn't count toward your plan's AI replies.
      </p>
      {unsaved && (
        <label className={styles.checkRow} style={{ margin: 'var(--inic-space-3) 0' }}>
          <input type="checkbox" checked={useDraft} onChange={(e) => setUseDraft(e.target.checked)} />
          <span className={styles.checkRowText}>
            <span className={styles.label} style={{ textTransform: 'none' }}>
              Use my unsaved changes from Company &amp; instructions
            </span>
            <span className={styles.hint}>Try them here before saving.</span>
          </span>
        </label>
      )}

      <div className={pg.chat}>
        <div className={pg.list} ref={listRef} role="log" aria-live="polite">
          {bubbles.length === 0 && (
            <div className={pg.empty}>
              <p>Start with a question{starters.length ? ', or one of your conversation starters:' : '.'}</p>
              {starters.length > 0 && (
                <div className={pg.chips}>
                  {starters.map((s) => (
                    <button key={s} type="button" className={pg.chip} onClick={() => void send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {bubbles.map((b, i) => (
            <div key={i} className={b.role === 'user' ? pg.rowUser : pg.rowBot}>
              <div className={b.role === 'user' ? pg.bubbleUser : pg.bubbleBot}>
                {b.richText?.length ? <RichText blocks={b.richText} /> : b.text}
                {b.readMore && b.readMore.length > 0 && (
                  <div className={pg.readMore}>
                    <span>Read more</span>
                    {b.readMore.map((l) => (
                      <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer">
                        {l.title} ↗
                      </a>
                    ))}
                  </div>
                )}
              </div>
              {b.role === 'assistant' && <AnswerFacts bubble={b} />}
            </div>
          ))}
          {busy && <div className={pg.typing}>Assistant is thinking…</div>}
        </div>
        <form
          className={pg.composer}
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
          }}
        >
          <input
            className={styles.input}
            value={input}
            maxLength={4000}
            placeholder="Ask something a visitor would ask…"
            onChange={(e) => setInput(e.target.value)}
            disabled={busy}
            aria-label="Test message"
          />
          <button type="submit" className={styles.button} disabled={busy || !input.trim()}>
            Send
          </button>
          <button
            type="button"
            className={styles.buttonGhost}
            disabled={busy || bubbles.length === 0}
            onClick={() => {
              setBubbles([]);
              setError(null);
            }}
          >
            New conversation
          </button>
        </form>
      </div>
      {error && <p className={styles.error} style={{ marginTop: 'var(--inic-space-3)' }}>{error}</p>}
    </section>
  );
}

/** What an answer was based on, and whether it would count as a gap. */
function AnswerFacts({ bubble }: { bubble: Bubble }) {
  const sources = bubble.citations ?? [];
  return (
    <div className={pg.facts}>
      {bubble.matched === null ? (
        <span>Data sources are off for this site, so this answer uses only the instructions and company profile.</span>
      ) : sources.length > 0 ? (
        <span>
          Based on:{' '}
          {sources.map((c, i) => (
            <span key={`${c.entryId ?? i}`}>
              {i > 0 && ', '}
              {c.sourceUrl ? (
                <a href={c.sourceUrl} target="_blank" rel="noopener noreferrer">
                  {c.title}
                </a>
              ) : (
                c.title
              )}
            </span>
          ))}
        </span>
      ) : (
        <span>No data source matched this question.</span>
      )}
      {bubble.gap && (
        <span className={pg.gap}>
          {bubble.gap === 'no-match'
            ? 'A visitor asking this would be added to Suggestions: nothing in your data sources covers it.'
            : 'A visitor asking this would be added to Suggestions: the assistant wasn’t sure.'}
        </span>
      )}
    </div>
  );
}
